import React, { useState, useMemo } from 'react'
import { useTheme } from '../../hooks/useTheme'
import { useDTEsReserva } from '../../hooks/useReservasManual'
import DTEModalManager from './DTEModalManager'

interface DTEReservasModalProps {
  isOpen: boolean
  onClose: () => void
  reservaId: string | null
}

const DTEReservasModal: React.FC<DTEReservasModalProps> = ({
  isOpen,
  onClose,
  reservaId,
}) => {
  const { colors } = useTheme()
  const [showDTEManager, setShowDTEManager] = useState(false)
  const [selectedPagoId, setSelectedPagoId] = useState<number | null>(null)
  const [selectedMontoForDTE, setSelectedMontoForDTE] = useState(0)

  // Obtener todos los DTEs de la reserva
  const { data: allDtes = [], isLoading: loadingDTEs, refetch: refetchDTEs } = useDTEsReserva(reservaId)

  // Agrupar DTEs por pago_id
  const pagosConDTEs = useMemo(() => {
    if (!allDtes || allDtes.length === 0) return []

    // Agrupar DTEs por pago_id
    const grouped: { [key: number]: any[] } = {}
    allDtes.forEach((dte: any) => {
      if (!grouped[dte.pago_id]) {
        grouped[dte.pago_id] = []
      }
      grouped[dte.pago_id].push(dte)
    })

    // Convertir a array con información del pago
    return Object.entries(grouped).map(([pagoId, dtes]) => ({
      id: parseInt(pagoId),
      dtes,
      monto: dtes[0]?.monto || 0,
      estado_pago: 'pagado', // Los DTEs solo existen para pagos realizados
    }))
  }, [allDtes])

  if (!isOpen) return null

  const handleOpenDTEManager = (pagoId: number, monto: number) => {
    setSelectedPagoId(pagoId)
    setSelectedMontoForDTE(monto)
    setShowDTEManager(true)
  }

  const handleCloseDTEManager = () => {
    setShowDTEManager(false)
    setSelectedPagoId(null)
    setSelectedMontoForDTE(0)
    refetchDTEs()
  }

  return (
    <>
      {/* Modal Principal - Lista de Pagos con DTEs */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="sticky top-0 flex items-center justify-between p-6 border-b"
            style={{ backgroundColor: colors.primary + '15' }}
          >
            <h2 className="text-xl font-bold" style={{ color: colors.primary }}>
              📄 DTEs - Pagos Registrados
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
            >
              ✕
            </button>
          </div>

          {/* Contenido */}
          <div className="p-6">
            {loadingDTEs ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: colors.primary }}></div>
              </div>
            ) : pagosConDTEs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className="text-lg">No hay pagos con DTEs registrados</p>
                <p className="text-sm mt-2">Los DTEs se muestran solo si existen registros asociados al pago</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pagosConDTEs.map((pago: any) => (
                  <div
                    key={pago.id}
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                    style={{ borderColor: colors.primary + '40' }}
                  >
                    {/* Información del Pago */}
                    <div className="flex items-center justify-between mb-4 pb-4 border-b" style={{ borderColor: colors.primary + '20' }}>
                      <div>
                        <p className="font-semibold text-gray-900">
                          Pago #{pago.id}
                        </p>
                        <p className="text-sm text-gray-600">
                          Monto: ${pago.monto.toLocaleString('es-CL')}
                        </p>
                        <p className="text-sm text-gray-600">
                          Estado: <span style={{ color: colors.primary }} className="font-medium">{pago.estado_pago}</span>
                        </p>
                      </div>
                      <button
                        onClick={() => handleOpenDTEManager(pago.id, pago.monto)}
                        className="px-3 py-2 rounded-lg text-sm font-medium text-white transition-opacity hover:opacity-90"
                        style={{ backgroundColor: colors.primary }}
                        title="Ver DTEs de este pago"
                      >
                        Ver DTEs →
                      </button>
                    </div>

                    {/* DTEs del Pago */}
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">DTEs Registrados ({pago.dtes.length}):</p>
                      <div className="space-y-2">
                        {pago.dtes.map((dte: any) => (
                          <div
                            key={dte.id}
                            className="bg-gray-50 rounded p-3 text-sm"
                            style={{ backgroundColor: colors.primary + '05' }}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-gray-900">
                                  {dte.tipo_documento.toUpperCase()} #{dte.numero_dte}
                                </p>
                                <p className="text-xs text-gray-600">
                                  Monto: ${dte.monto.toLocaleString('es-CL')} •
                                  Estado: <span style={{ color: colors.primary }} className="font-medium">{dte.status_dte}</span>
                                </p>
                              </div>
                              {dte.url_documento && (
                                <a
                                  href={dte.url_documento}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                                >
                                  📥 Descargar
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg font-medium text-gray-700 border border-gray-300 hover:bg-gray-100 transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>

      {/* Modal DTEModalManager - Nested para ver/agregar DTEs de un pago específico */}
      {showDTEManager && (
        <DTEModalManager
          isOpen={showDTEManager}
          onClose={handleCloseDTEManager}
          reservaId={reservaId}
          pagoId={selectedPagoId}
          monto={selectedMontoForDTE}
        />
      )}
    </>
  )
}

export default DTEReservasModal
